'use client';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { encodeFunctionData, erc20Abi, maxUint256 } from 'viem';

import { useAuth, usePolymarketClient } from '@/hooks';
import { executeRelayMetaTxBatch } from '@/utils/relayerBatch.utils';

import {
  COLLATERAL_OFFRAMP_ADDRESS,
  COLLATERAL_ONRAMP_ADDRESS,
  NATIVE_USDC_CONTRACT_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

/**
 * useCollateralOnramp - Hook to handle the migration from USDC.e to pUSD (v2 collateral).
 * Interacts with the Polymarket Collateral Onramp contract on Polygon.
 */
export function useCollateralOnramp() {
  const { user } = useAuth();
  const { client: polymarketRelayClient } = usePolymarketClient();
  const [isPending, setIsPending] = useState(false);
  const queryClient = useQueryClient();

  /**
   * Wraps USDC.e tokens into pUSD via the CollateralOnramp contract.
   *
   * @param amount - The amount of USDC.e to wrap (in native decimals, usually 6).
   */
  const wrap = useCallback(
    async (amount: bigint) => {
      if (!user?.proxyWalletAddress) throw new Error('Safe not initialized');
      if (!polymarketRelayClient) throw new Error('Relay client not initialized');

      setIsPending(true);
      try {
        const depositData = encodeFunctionData({
          abi: [
            {
              name: 'wrap',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'asset', type: 'address' },
                { name: 'to', type: 'address' },
                { name: 'amount', type: 'uint256' },
              ],
              outputs: [],
            },
          ],
          args: [
            USDC_E_CONTRACT_ADDRESS as `0x${string}`,
            user.proxyWalletAddress as `0x${string}`,
            amount,
          ],
        });

        const approveData = encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [COLLATERAL_ONRAMP_ADDRESS as `0x${string}`, maxUint256],
        });

        const txApprove = {
          to: USDC_E_CONTRACT_ADDRESS as `0x${string}`,
          data: approveData,
          value: '0',
        };

        const txWrap = {
          to: COLLATERAL_ONRAMP_ADDRESS as `0x${string}`,
          data: depositData,
          value: '0',
        };

        const response = await executeRelayMetaTxBatch(
          polymarketRelayClient as any,
          user.proxyWalletAddress,
          user.tradingWalletKind,
          [txApprove, txWrap].map((txn) => ({
            to: txn.to,
            data: txn.data,
            value: txn.value ?? '0',
          })),
          `Upgrade ${Number(amount) / 1e6} USDC.e to pUSD`
        );

        // toast.info('Collateral upgrade transaction submitted...');
        await (response as any).wait();

        // Invalidate balances to trigger UI refresh
        queryClient.invalidateQueries({ queryKey: ['portfolio', 'usdc-balance'] });
        queryClient.invalidateQueries({ queryKey: ['portfolio', 'pusd-balance'] });
        queryClient.invalidateQueries({ queryKey: ['portfolio', 'summary'] });

        // toast.success('Successfully upgraded to pUSD!');

        return { success: true };
      } catch (error: any) {
        console.error('[useCollateralOnramp] Wrap failed:', error);

        let msg = error?.message || 'Failed to upgrade collateral';
        if (msg.toLowerCase().includes('user rejected') || error?.code === 4001) {
          msg = 'Transaction rejected. Upgrade cancelled.';
        }

        // toast.error(msg);
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    [user, polymarketRelayClient, queryClient]
  );

  /**
   * Wraps native USDC (Circle CCTP, 0x3c499...) held by the proxy Safe into pUSD.
   *
   * Native USDC requires an explicit approval step because initializeProxy only
   * sets up USDC.e approval. We batch approve + wrap in a single Safe transaction.
   *
   * @param amount - Amount of native USDC to wrap (6 decimals).
   */
  const wrapNativeUsdc = useCallback(
    async (amount: bigint) => {
      if (!user?.proxyWalletAddress) throw new Error('Safe not initialized');
      if (!polymarketRelayClient) throw new Error('Relay client not initialized');

      setIsPending(true);
      try {
        // Batch 1: approve CollateralOnramp to spend proxy's native USDC
        const approveData = encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [COLLATERAL_ONRAMP_ADDRESS as `0x${string}`, maxUint256],
        });

        // Batch 2: wrap native USDC → pUSD
        const wrapData = encodeFunctionData({
          abi: [
            {
              name: 'wrap',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'asset', type: 'address' },
                { name: 'to', type: 'address' },
                { name: 'amount', type: 'uint256' },
              ],
              outputs: [],
            },
          ],
          args: [
            NATIVE_USDC_CONTRACT_ADDRESS as `0x${string}`,
            user.proxyWalletAddress as `0x${string}`,
            amount,
          ],
        });

        const response = await executeRelayMetaTxBatch(
          polymarketRelayClient as any,
          user.proxyWalletAddress,
          user.tradingWalletKind,
          [
            { to: NATIVE_USDC_CONTRACT_ADDRESS as string, data: approveData, value: '0' },
            { to: COLLATERAL_ONRAMP_ADDRESS as string, data: wrapData, value: '0' },
          ],
          `Upgrade ${Number(amount) / 1e6} native USDC to pUSD`
        );

        // toast.info('Converting native USDC to pUSD…');
        await (response as any).wait();

        queryClient.invalidateQueries({ queryKey: ['portfolio'] });
        // toast.success('Successfully converted native USDC to pUSD!');
        return { success: true };
      } catch (error: any) {
        console.error('[useCollateralOnramp] Native USDC wrap failed:', error);
        const msg = error?.message || 'Failed to convert native USDC';
        // toast.error(msg);
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    [user, polymarketRelayClient, queryClient]
  );

  /**
   * Unwraps pUSD back into USDC.e via the CollateralOnramp contract.
   *
   * @param amount - The amount of pUSD to unwrap (6 decimals).
   */
  const unwrap = useCallback(
    async (amount: bigint) => {
      if (!user?.proxyWalletAddress) throw new Error('Safe not initialized');
      if (!polymarketRelayClient) throw new Error('Relay client not initialized');

      setIsPending(true);
      try {
        const approveData = encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [COLLATERAL_OFFRAMP_ADDRESS as `0x${string}`, amount],
        });

        const unwrapData = encodeFunctionData({
          abi: [
            {
              name: 'unwrap',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'underlyingToken', type: 'address' },
                { name: 'recipient', type: 'address' },
                { name: 'amount', type: 'uint256' },
              ],
              outputs: [],
            },
          ],
          args: [
            USDC_E_CONTRACT_ADDRESS as `0x${string}`,
            user.proxyWalletAddress as `0x${string}`,
            amount,
          ],
        });

        const response = await (polymarketRelayClient as any).executeSafeTransactions(
          [
            {
              to: PUSD_CONTRACT_ADDRESS as `0x${string}`,
              data: approveData,
              value: '0',
              operation: 0,
            },
            {
              to: COLLATERAL_OFFRAMP_ADDRESS as `0x${string}`,
              data: unwrapData,
              value: '0',
              operation: 0,
            },
          ],
          `Unwrap ${Number(amount) / 1e6} pUSD to USDC.e`
        );

        // toast.info('Collateral unwrap transaction submitted...');
        await (response as any).wait();

        // Invalidate balances
        queryClient.invalidateQueries({ queryKey: ['portfolio'] });

        // toast.success('Successfully unwrapped pUSD!');

        return { success: true };
      } catch (error: any) {
        console.error('[useCollateralOnramp] Unwrap failed:', error);
        toast.error(error?.message || 'Failed to unwrap collateral');
        throw error;
      } finally {
        setIsPending(false);
      }
    },
    [user, polymarketRelayClient, queryClient]
  );

  return {
    wrap,
    wrapNativeUsdc,
    unwrap,
    isPending,
  };
}
